/**
 * The core model. [P1-tiles]
 *
 * ── The sub-edge model, and why it exists ────────────────────────────────────────────────────────
 *
 * Every tile side is divided into THREE sub-edges. This is the whole trick, and it is what makes
 * farmer scoring possible:
 *
 *        0  1  2                A road running through a side occupies only the CENTRE sub-edge.
 *      ┌──────────┐             The two outer sub-edges are field — and they are SEPARATE fields,
 *   11 │          │ 3           because the road divides them. That single fact is the thing most
 *   10 │    N     │ 4           implementations get wrong, and it is why we cannot model a side as
 *    9 │ W     E  │ 5           one atomic terrain.
 *      │    S     │
 *      └──────────┘             Sub-edge indices run CLOCKWISE from the north-west corner, 0..11:
 *        8  7  6                  N = 0,1,2   E = 3,4,5   S = 6,7,8   W = 9,10,11
 *
 * A side's terrain determines its sub-edge terrains — derived, never authored:
 *      CITY  → [city,  city, city ]
 *      ROAD  → [field, road, field]
 *      FIELD → [field, field, field]
 *
 * When two tiles meet, their touching sub-edges pair up in REVERSE order (my north 0,1,2 touches my
 * neighbour's south 8,7,6), and each matched pair unions the two features that own those sub-edges.
 * Cities join cities, roads join roads, and — critically — fields join fields, which is how a farm
 * grows across the board.
 */
export const posKey = (p) => `${p.x},${p.y}`;
export function posFromKey(key) {
    const comma = key.indexOf(',');
    return { x: Number(key.slice(0, comma)), y: Number(key.slice(comma + 1)) };
}
/** Sides, in clockwise order. The numeric values are load-bearing: `side * 3` is its first sub-edge. */
export var Side;
(function (Side) {
    Side[Side["North"] = 0] = "North";
    Side[Side["East"] = 1] = "East";
    Side[Side["South"] = 2] = "South";
    Side[Side["West"] = 3] = "West";
})(Side || (Side = {}));
export const SIDES = [Side.North, Side.East, Side.South, Side.West];
/** The neighbour offset for each side. y grows northward. */
export const SIDE_OFFSET = {
    [Side.North]: { x: 0, y: 1 },
    [Side.East]: { x: 1, y: 0 },
    [Side.South]: { x: 0, y: -1 },
    [Side.West]: { x: -1, y: 0 },
};
/** The side that faces a given side across a tile boundary. */
export const OPPOSITE = {
    [Side.North]: Side.South,
    [Side.East]: Side.West,
    [Side.South]: Side.North,
    [Side.West]: Side.East,
};
/** The three sub-edge terrains implied by a side's terrain. Derived — see the header diagram. */
export const SUBEDGE_TERRAIN = {
    city: ['city', 'city', 'city'],
    road: ['field', 'road', 'field'],
    field: ['field', 'field', 'field'],
};
export const MEEPLES_PER_PLAYER = 7;
/**
 * How many followers a meeple counts for when deciding who holds a majority.
 *
 * The big follower is the reason this is a weight and not a count: it is one meeple that speaks with
 * two voices. Pigs and abbots never contest a majority in the usual way, but they are still one body
 * each, so the table stays total rather than special-casing at every call site.
 */
export const MEEPLE_STRENGTH = {
    follower: 1,
    big: 2,
    abbot: 1,
    wagon: 1,
    pig: 1,
};
/** The base game's starting supply: seven plain followers and nothing else. */
export const BASE_SUPPLY = { follower: MEEPLES_PER_PLAYER };
export const supplyOf = (supply, kind) => supply?.[kind] ?? 0;
/** Every meeple a seat still holds, of any kind — what a player means by "meeples left". */
export const totalSupply = (supply) => Object.values(supply ?? {}).reduce((a, b) => a + (b ?? 0), 0);
export const ok = (value) => ({ ok: true, value });
export const err = (code, message) => ({
    ok: false,
    error: { code, message },
});
//# sourceMappingURL=types.js.map